import _ from "lodash";

import * as FileRenameJobsCRUD from "app/crud/file-rename-jobs.crud";
import { setUpgradeDialog } from "app/store/modules/actions/app.actions";
import { setErrorFlag, setLoadingFlag } from "app/store/modules/actions/loading-flag.actions";
import { downloadFileTask, uploadFileTask } from "app/store/modules/actions/tasks.actions";

export const actionTypes = {
  FILE_RENAME_JOBS_LOADED: "[FILE_RENAME_JOBS] FILE_RENAME_JOBS_LOADED",
  FILE_RENAME_JOB_CREATED: "[FILE_RENAME_JOBS] FILE_RENAME_JOB_CREATED",
  FILE_RENAME_JOB_LOADED: "[FILE_RENAME_JOBS] FILE_RENAME_JOB_LOADED",
  FILE_RENAME_JOB_UPDATED: "[FILE_RENAME_JOBS] FILE_RENAME_JOB_UPDATED",
  FILE_RENAME_JOB_CLEARED: "[FILE_RENAME_JOBS] FILE_RENAME_JOB_CLEARED",
  CHANGE_PAGE: "[FILE_RENAME_JOBS] CHANGE_PAGE",
  UPDATE_FILTER: "[FILE_RENAME_JOBS] UPDATE_FILTER",
  SELECT_FILE_RENAME_JOB: "[FILE_RENAME_JOBS] SELECT_FILE_RENAME_JOB",
  UNSELECT_FILE_RENAME_JOB: "[FILE_RENAME_JOBS] UNSELECT_FILE_RENAME_JOB",
  FILE_RENAME_JOBS_ARCHIVED: "[FILE_RENAME_JOBS] FILE_RENAME_JOBS_ARCHIVED",
  FILE_RENAME_JOBS_UNARCHIVED: "[FILE_RENAME_JOBS] FILE_RENAME_JOBS_UNARCHIVED",
  FILE_RENAME_JOBS_DELETED: "[FILE_RENAME_JOBS] FILE_RENAME_JOBS_DELETED",
  FILE_RENAME_JOB_SECTION_UPDATED: "[FILE_RENAME_JOBS] FILE_RENAME_JOB_SECTION_UPDATED",
  FILE_RENAME_JOBS_FILE_STATUS_UPDATED: "[FILE_RENAME_JOBS] FILE_RENAME_JOBS_FILE_STATUS_UPDATED",
};

export const flagNames = {
  FILE_RENAME_JOBS: "[FILE_RENAME_JOBS] FILE_RENAME_JOBS",
  FILE_RENAME_JOB: "[FILE_RENAME_JOBS] FILE_RENAME_JOB",
  CREATE: "[FILE_RENAME_JOBS] CREATE",
  UPDATE: "[FILE_RENAME_JOBS] UPDATE",
  RERUN_AI: "[FILE_RENAME_JOBS] RERUN_AI",
  UPLOAD: "[FILE_RENAME_JOBS] UPLOAD",
  ARCHIVE: "[FILE_RENAME_JOBS] ARCHIVE",
  DELETE: "[FILE_RENAME_JOBS] DELETE",
  DOWNLOAD_ALL_FILES: "[FILE_RENAME_JOBS] DOWNLOAD_ALL_FILES",
};

// Load file rename jobs
export const loadFileRenameJobs = () => {
  return async (dispatch, getState) => {
    dispatch(setLoadingFlag(flagNames.FILE_RENAME_JOBS, true));

    try {
      const state = getState();
      const filter = state.fileRenameJobs?.filter || {};

      const statusesToLoad = filter.archived ? ["ARCHIVED"] : ["PENDING", "COMPLETED"];

      const response = await FileRenameJobsCRUD.getFileRenameJobs(statusesToLoad);
      const { status, data } = response;

      if (status === 200) {
        dispatch({
          type: actionTypes.FILE_RENAME_JOBS_LOADED,
          payload: {
            list: data,
          },
        });
      } else {
        dispatch(
          setErrorFlag(
            flagNames.FILE_RENAME_JOBS,
            data.message || "Failed to load file renaming jobs"
          )
        );
      }

      dispatch(setLoadingFlag(flagNames.FILE_RENAME_JOBS, false));
      return Promise.resolve({ status, data });
    } catch (e) {
      const errorMessage = e?.response?.data?.error?.message || e.toString();
      dispatch(setErrorFlag(flagNames.FILE_RENAME_JOBS, errorMessage));
      dispatch(setLoadingFlag(flagNames.FILE_RENAME_JOBS, false));
      return Promise.reject(e);
    }
  };
};

export const createFileRenameJob = (payload) => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.CREATE, true));
    try {
      const { data, status } = await FileRenameJobsCRUD.createFileRenameJob(payload);
      dispatch({
        type: actionTypes.FILE_RENAME_JOB_CREATED,
        payload: {
          fileRenameJob: data.data,
        },
      });
      dispatch(setLoadingFlag(flagNames.CREATE, false));
      return Promise.resolve({ status, data });
    } catch (e) {
      dispatch(setErrorFlag(flagNames.CREATE, e.toString()));
      dispatch(setLoadingFlag(flagNames.CREATE, false));
      return Promise.reject(e);
    }
  };
};

export const loadFileRenameJob = (id) => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.FILE_RENAME_JOB, true));
    try {
      const { data: fileRenameJob, status } = await FileRenameJobsCRUD.getFileRenameJob(id);
      dispatch({
        type: actionTypes.FILE_RENAME_JOB_LOADED,
        payload: {
          fileRenameJob,
        },
      });
      dispatch(setLoadingFlag(flagNames.FILE_RENAME_JOB, false));
      return Promise.resolve({ status, data: fileRenameJob });
    } catch (e) {
      const errorMessage = e?.response?.data?.error?.message || e.toString();
      dispatch(setErrorFlag(flagNames.FILE_RENAME_JOB, errorMessage));
      return Promise.reject(e);
    }
  };
};

export const updateFileRenameJob = (id, payload) => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.UPDATE, true));

    try {
      const { data, status } = await FileRenameJobsCRUD.updateFileRenameJob(id, payload);
      dispatch({
        type: actionTypes.FILE_RENAME_JOB_UPDATED,
        payload: {
          fileRenameJob: data.data,
        },
      });
      dispatch(setLoadingFlag(flagNames.UPDATE, false));
      return Promise.resolve({ status, data });
    } catch (e) {
      const errorMessage = e?.response?.data?.error?.message || e.toString();
      dispatch(setErrorFlag(flagNames.UPDATE, errorMessage));
      dispatch(setLoadingFlag(flagNames.UPDATE, false));
      return Promise.reject(e);
    }
  };
};

export const rerunAiFileRenameForFileRenameJob = (id, payload) => {
  return async (dispatch, getStore) => {
    const { sectionId, fileId } = payload || {};
    const store = getStore();

    if (!id || !sectionId || !fileId) {
      const errorMessage = "Missing file rename job/section/file identifiers";
      dispatch(setErrorFlag(flagNames.RERUN_AI, errorMessage));
      return Promise.resolve({
        status: 400,
        data: {
          message: errorMessage,
        },
      });
    }

    dispatch(setErrorFlag(flagNames.RERUN_AI, null));
    dispatch(setLoadingFlag(flagNames.RERUN_AI, true));

    try {
      const { data, status } = await FileRenameJobsCRUD.rerunAiFileRename(id, {
        sectionId,
        fileId,
      });

      const fileRenameJob = store.fileRenameJobs?.selectedFileRenameJob;

      // Update the file rename status to PENDING in the store to reflect that the AI renaming is being reprocessed
      for (const section of fileRenameJob.sections) {
        if (section._id === sectionId) {
          for (const file of section.files) {
            if (file._id === fileId) {
              file.fileRenameStatus = "PENDING";
              break;
            }
          }
          break;
        }
      }

      if (status === 200 && data?.data?._id) {
        dispatch({
          type: actionTypes.FILE_RENAME_JOB_UPDATED,
          payload: {
            fileRenameJob,
          },
        });
      } else if (status !== 200) {
        dispatch(setErrorFlag(flagNames.RERUN_AI, data?.message || "Failed to reapply AI rename"));
      }

      return Promise.resolve({ status, data });
    } catch (e) {
      const errorMessage = e?.response?.data?.error?.message || e.toString();
      dispatch(setErrorFlag(flagNames.RERUN_AI, errorMessage));
      return Promise.reject(e);
    } finally {
      dispatch(setLoadingFlag(flagNames.RERUN_AI, false));
    }
  };
};

export const setPage = (page) => {
  return {
    type: actionTypes.CHANGE_PAGE,
    payload: {
      page,
    },
  };
};

export const updateFileRenameJobsFilter = (data) => {
  return {
    type: actionTypes.UPDATE_FILTER,
    payload: {
      data,
    },
  };
};

export const selectFileRenameJobItem = (fileRenameJob) => {
  return {
    type: actionTypes.SELECT_FILE_RENAME_JOB,
    payload: {
      fileRenameJob,
    },
  };
};

export const unselectFileRenameJobItem = (fileRenameJob) => {
  return {
    type: actionTypes.UNSELECT_FILE_RENAME_JOB,
    payload: {
      fileRenameJob,
    },
  };
};

export const deleteFileRenameJobs = (fileRenameJobs) => {
  return async (dispatch) => {
    const fileRenameJobsToDelete = fileRenameJobs.filter((job) => {
      const status = (job?.status || "").toString().toUpperCase();
      return job.archived || status === "ARCHIVED";
    });
    const jobIds = fileRenameJobsToDelete.map((job) => job._id);

    if (!jobIds.length) {
      return;
    }

    dispatch(setLoadingFlag(flagNames.DELETE, true));

    try {
      await Promise.all(jobIds.map((jobId) => FileRenameJobsCRUD.deleteFileRenameJob(jobId)));
      const status = 200;
      const data = { message: "Success" };

      if (status === 200) {
        dispatch({
          type: actionTypes.FILE_RENAME_JOBS_DELETED,
          payload: {
            requestIds: jobIds,
          },
        });
      } else {
        dispatch(setErrorFlag(flagNames.DELETE, data.message));
      }

      return Promise.resolve({ status, data });
    } catch (e) {
      const errorMessage = _.get(e, "response.data.error.message", e.toString());
      dispatch(setErrorFlag(flagNames.DELETE, errorMessage));
      return Promise.reject(e);
    } finally {
      dispatch(setLoadingFlag(flagNames.DELETE, false));
    }
  };
};

export const uploadFilesToFileRenameSection = (id, sectionId, files) => {
  return async (dispatch, getStore) => {
    const store = getStore();
    const selectedFileRenameJob = _.get(store, "fileRenameJobs.selectedFileRenameJob", {});
    const restrictions = _.get(store, "auth.restrictions.data", {});

    if (!restrictions.storageAllowed) {
      dispatch(setUpgradeDialog("USER.UPLOAD.PLAN_LIMIT_REACHED"));
      return;
    }

    const selectedSection = selectedFileRenameJob?.sections?.find?.(
      (section) => section._id === sectionId
    );

    if (!selectedSection) {
      dispatch(setErrorFlag(flagNames.UPDATE, "Section not found"));
      return Promise.resolve({ status: 400, data: {} });
    }

    const fileData = [];

    dispatch(setLoadingFlag(flagNames.UPLOAD, sectionId));

    try {
      const { data: registeredFiles } = await FileRenameJobsCRUD.uploadFilesToFileRenameJobSection(
        id,
        sectionId,
        files
      );

      for (let i = 0; i < files.length; i++) {
        const registeredFile = registeredFiles[i];
        const file = files[i];

        const { key, name: uploadedFileName, signedRequest, url } = registeredFile;

        const response = await dispatch(uploadFileTask(signedRequest, file));

        if (response.wasCancelled) {
          continue;
        }

        fileData.push({
          key,
          name: uploadedFileName,
          url,
          size: file.size / 1024,
        });
      }

      // Update the job after appending the new files to the section
      const updatedJob = _.cloneDeep(selectedFileRenameJob);
      updatedJob.sections = updatedJob.sections.map((section) => {
        if (section._id === sectionId) {
          return {
            ...section,
            files: [...section.files, ...fileData],
          };
        }

        return section;
      });

      const { status, data } = await dispatch(updateFileRenameJob(id, updatedJob));

      dispatch(setLoadingFlag(flagNames.UPLOAD, false));
      return Promise.resolve({ status, data });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      const errorMessage = _.get(e, "response.data.error.message", e.toString());
      dispatch(setErrorFlag(flagNames.UPLOAD, errorMessage));
      return Promise.reject(e);
    }
  };
};

export const updateFileRenameJobFileStatus = (sectionId, fileId, fileObject) => {
  return {
    type: actionTypes.FILE_RENAME_JOBS_FILE_STATUS_UPDATED,
    payload: {
      sectionId,
      fileId,
      fileObject,
    },
  };
};

export const clearSelectedFileRenameJob = () => {
  return {
    type: actionTypes.FILE_RENAME_JOB_CLEARED,
  };
};

export const downloadAllFilesForFileRenameJob = (id) => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.DOWNLOAD_ALL_FILES, true));
    try {
      const { status, data } = await FileRenameJobsCRUD.downloadAllFilesForJob(id);

      if (status === 200) {
        const { name, url } = data;

        await dispatch(downloadFileTask(url, name));

        return Promise.resolve({ status, data });
      } else {
        dispatch(setErrorFlag(flagNames.DOWNLOAD_ALL_FILES, data.message));
      }
    } catch (e) {
      const errorMessage = e?.response?.data?.error?.message || e.toString();
      dispatch(setErrorFlag(flagNames.DOWNLOAD_ALL_FILES, errorMessage));
      return Promise.reject(e);
    } finally {
      dispatch(setLoadingFlag(flagNames.DOWNLOAD_ALL_FILES, false));
    }
  };
};
